'use client';

import { cn } from '@ais-ui/utils';
import { CalendarIcon } from '@radix-ui/react-icons';
import { format, isValid } from 'date-fns';
import * as React from 'react';
import { Button } from './Button';
import { Calendar } from './Calendar';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './Select';
import { useMemo } from 'react';

interface DatePickerProps {
  value: Date;
  onChange: (date: Date | undefined) => void;
  disabled?: boolean;
  disabledCalendar?: (date: Date) => boolean;
}
export const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(
  ({ value, onChange, disabled = false, disabledCalendar, ...props }, ref) => {
    const getInitialDate = (val: string | Date) => {
      if (typeof val === 'string') {
        // Parse ISO string to Date
        const parsedDate = new Date(val);
        return isValid(parsedDate) ? parsedDate : undefined;
      } else if (val instanceof Date) {
        return val;
      } else {
        return undefined;
      }
    };
    const [date, setDate] = React.useState<Date | undefined>(
      getInitialDate(value),
    );

    React.useEffect(() => {
      setDate(getInitialDate(value));
    }, [value]);

    const handleDateSelect = (newDate: Date | undefined) => {
      setDate(newDate);
      if (onChange) {
        onChange(newDate);
      }
    };

    const formattedDate =
      date && isValid(date) ? format(date, 'PPP') : 'Pick a date';

    const currentYear = useMemo(() => new Date().getFullYear(), []);
    const fromYear = useMemo(() => currentYear, [currentYear]);
    const toYear = useMemo(() => currentYear + 5, [currentYear]);

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant={'outline'}
            disabled={disabled}
            className={cn(
              'w-full justify-start text-left font-normal',
              !date && 'text-muted-foreground',
            )}
          >
            <CalendarIcon className="mr-2 size-4" />
            {formattedDate}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            captionLayout="dropdown"
            selected={date}
            onSelect={handleDateSelect}
            initialFocus
            disabled={disabledCalendar}
            fromYear={fromYear}
            toYear={toYear}
            {...props}
          />
        </PopoverContent>
      </Popover>
    );
  },
);

interface DateTimePickerProps {
  value: Date;
  onChange: (date: Date | undefined) => void;
}

export const DateTimeSelectPicker = React.forwardRef<
  HTMLButtonElement,
  DateTimePickerProps
>(({ value, onChange, ...props }, ref) => {
  const getInitialDate = (val: string | Date) => {
    if (typeof val === 'string') {
      // Parse ISO string to Date
      const parsedDate = new Date(val);
      return isValid(parsedDate) ? parsedDate : undefined;
    } else if (val instanceof Date) {
      return val;
    } else {
      return undefined;
    }
  };
  const [date, setDate] = React.useState<Date | undefined>(
    getInitialDate(value),
  );

  React.useEffect(() => {
    setDate(getInitialDate(value));
  }, [value]);

  const handleTimeChange = (type: 'hours' | 'minutes', value: string) => {
    const number = parseInt(value, 10);
    if (isNaN(number)) return; // Exit if the value is not a number

    if (date) {
      const updatedDate = new Date(date);

      if (type === 'hours') {
        updatedDate.setHours(number);
      } else if (type === 'minutes') {
        updatedDate.setMinutes(number);
      }

      setDate(updatedDate);

      if (onChange) {
        onChange(updatedDate);
      }
    }
  };

  const handleDateSelect = (newDate: Date | undefined) => {
    if (newDate && date && isValid(date)) {
      // Preserve the existing hours and minutes
      newDate.setHours(date.getHours());
      newDate.setMinutes(date.getMinutes());
    }

    setDate(newDate);

    if (onChange) {
      onChange(newDate);
    }
  };

  const formattedDateTime =
    date && isValid(date)
      ? `${format(date, 'PPP')} ${date
          .getHours()
          .toString()
          .padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
      : 'Pick a date and time';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          variant={'outline'}
          className={cn(
            'w-full justify-start text-left font-normal',
            !date && 'text-muted-foreground',
          )}
        >
          <CalendarIcon className="mr-2 size-4" />
          {formattedDateTime}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleDateSelect}
          initialFocus
          disabled={(date) => date > new Date()}
          {...props}
        />
        <div className="flex space-x-2 p-2">
          <Select
            value={
              date && isValid(date) ? date.getHours().toString() : undefined
            }
            onValueChange={(value) => handleTimeChange('hours', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Hour" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Hours</SelectLabel>
                {Array.from({ length: 24 }, (_, i) => (
                  <SelectItem key={i} value={i.toString()}>
                    {i.toString().padStart(2, '0')}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          <Select
            value={
              date && isValid(date) ? date.getMinutes().toString() : undefined
            }
            onValueChange={(value) => handleTimeChange('minutes', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Minute" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Minutes</SelectLabel>
                {Array.from({ length: 60 }, (_, i) => (
                  <SelectItem key={i} value={i.toString()}>
                    {i.toString().padStart(2, '0')}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </PopoverContent>
    </Popover>
  );
});
